import { ActionNames, DrawingAction }
  from '../actions/DrawingAction';
import {
  ShapeInfo, nullShape, ShapeKind, Tiling, Point, PointName, Offset, isPointEqual,
  getRectCoveringAllPoints, StraightLineShape, getAxisAlignedLineFromPoints, BalloonShape,
  TransformDelta, unnamedPoint,
} from '../types';
import { imageTileSize, fundamentalSizeMultiplier } from '../constants';
import { PictureStorageState } from './pictureStorage';

export interface DrawingLayer {
  readonly shape: ShapeInfo;
  readonly currentImageTiling: Tiling<string>;
  readonly imageOffsetX: number;
  readonly imageOffsetY: number;
}

function visitPictureReferencesInDrawingLayer(src: DrawingLayer, callback: (id: string) => void) {
  src.currentImageTiling.forEach((_row, _column, pictureID) => {
    callback(pictureID);
  });

  const { shape } = src;
  if (shape.kind === ShapeKind.ImageFile) {
    callback(shape.pictureID);
  }
}

const emptyImageTiling = new Tiling<string>();

export const blankDrawingLayer: DrawingLayer = {
  shape: nullShape,
  currentImageTiling: emptyImageTiling,
  imageOffsetX: 0,
  imageOffsetY: 0,
};

interface JSONReadyImageTile {
  readonly row: number;
  readonly column: number;
  readonly pictureID: string;
}

interface JSONReadyDrawingLayer {
  readonly shape: ShapeInfo;
  readonly tiles: ReadonlyArray<JSONReadyImageTile>;
  readonly imageOffsetX: number;
  readonly imageOffsetY: number;
}

function visitPictureReferencesInJSONReadyDrawingLayer(
  src: JSONReadyDrawingLayer,
  callback: (id: string) => void,
) {
  for (const tile of src.tiles) {
    callback(tile.pictureID);
  }

  const { shape } = src;
  if (shape.kind === ShapeKind.ImageFile) {
    callback(shape.pictureID);
  }
}

function jsonizeDrawingLayer(src: DrawingLayer): JSONReadyDrawingLayer {
  const tiles: JSONReadyImageTile[] = [];

  src.currentImageTiling.forEach((row, column, pictureID) => {
    tiles.push({ row, column, pictureID });
  });

  const { imageOffsetX, imageOffsetY, shape } = src;
  return { tiles, imageOffsetX, imageOffsetY, shape };
}

function dejsonizeDrawingLayer(src: JSONReadyDrawingLayer): DrawingLayer {
  let currentImageTiling = new Tiling<string>();

  for (const tile of src.tiles) {
    currentImageTiling = currentImageTiling.plus(tile.row, tile.column, tile.pictureID);
  }

  const { imageOffsetX, imageOffsetY, shape } = src;
  return { currentImageTiling, imageOffsetX, imageOffsetY, shape };
}

type ResourceReferenceState = { readonly [id: string]: number };

function incrementReference(state: ResourceReferenceState, id: string): ResourceReferenceState {
  const refCount = id in state ? state[id] + 1 : 1;
  return { ...state, [id]: refCount };
}

function decrementReference(state: ResourceReferenceState, id: string): ResourceReferenceState {
  if (!(id in state)) {
    throw new Error('Invalid argument.');
  }

  const nextState: { [id: string]: number } = { ...state };
  if (state[id] === 1) {
    delete nextState[id];
  } else {
    nextState[id] -= 1;
  }

  return nextState;
}

export interface DrawingState {
  readonly layers: { readonly [layerId: number]: DrawingLayer };
  readonly layerOrder: ReadonlyArray<number>; // bottom to top
  readonly nextLayerId: number;
  readonly unusedLayerIds: ReadonlyArray<number>;
  readonly activeLayer: number;
  readonly pictureReferences: ResourceReferenceState;
  readonly showObjects: Boolean;
  readonly showLearningPenRect: Boolean;
}

export function collectPictureIDsFromDrawingState(src: DrawingState) {
  return Object.keys(src.pictureReferences);
}

export const bottomLayerID = 0;

export const blankDrawingState = {
  layers: { [bottomLayerID]: blankDrawingLayer },
  layerOrder: [bottomLayerID],
  nextLayerId: bottomLayerID + 1,
  unusedLayerIds: [],
  activeLayer: bottomLayerID,
  pictureReferences: {},
  showLearningPenRect: true,
  showObjects: true,
};

export interface JSONReadyDrawingData {
  readonly layers: ReadonlyArray<JSONReadyDrawingLayer>;
  readonly activeLayer: number;
}

export function collectPictureIDsFromJSONReadyDrawingData(src: JSONReadyDrawingData) {
  const ids: { [id: string]: true } = {};

  for (const layer of src.layers) {
    visitPictureReferencesInJSONReadyDrawingLayer(layer, id => ids[id] = true);
  }

  return Object.keys(ids);
}

export function jsonizeDrawingData(src: DrawingState): JSONReadyDrawingData {
  const layers: JSONReadyDrawingLayer[] = [];
  let activeLayer = -1;

  for (const layerID of src.layerOrder) {
    if (layerID === src.activeLayer) {
      activeLayer = layers.length;
    }

    layers.push(jsonizeDrawingLayer(src.layers[layerID]));
  }

  return { layers, activeLayer };
}

export function dejsonizeDrawingData(src: JSONReadyDrawingData): DrawingState {
  const layers: { [layerID: number]: DrawingLayer } = {};
  const layerOrder: number[] = [];
  let pictureReferences: ResourceReferenceState = {};

  for (let index = 0; index < src.layers.length; index += 1) {
    const layer = src.layers[index];

    layers[index] = dejsonizeDrawingLayer(layer);
    layerOrder.push(index);

    visitPictureReferencesInJSONReadyDrawingLayer(layer, (id) => {
      pictureReferences = incrementReference(pictureReferences, id);
    });
  }

  return {
    layers,
    layerOrder,
    pictureReferences,
    nextLayerId: src.layers.length,
    unusedLayerIds: [],
    activeLayer: src.activeLayer,
    showObjects: true,
    showLearningPenRect: true,
  };
}

const init = (): DrawingState => ({
  layers: {
    0: blankDrawingLayer,
  },
  layerOrder: [0],
  nextLayerId: 1,
  unusedLayerIds: [],
  activeLayer: 0,
  pictureReferences: {},
  showObjects: true,
  showLearningPenRect: true,
});

const appendLayer = (prevState: DrawingState, shape: ShapeInfo): DrawingState => {
  let newLayerId: number;
  let nextLayerId = prevState.nextLayerId;
  let unusedLayerIds = prevState.unusedLayerIds;
  const layers: { [layerId: number]: DrawingLayer } = { ...prevState.layers };
  const layerOrder = prevState.layerOrder.slice();

  if (unusedLayerIds.length === 0) {
    newLayerId = nextLayerId;
    nextLayerId += 1;
  } else {
    newLayerId = unusedLayerIds[0];
    unusedLayerIds = unusedLayerIds.slice(1);
  }

  layers[newLayerId] = {
    shape,
    currentImageTiling: emptyImageTiling,
    imageOffsetX: 0,
    imageOffsetY: 0,
  };

  layerOrder.push(newLayerId);

  return {
    ...prevState,
    layers,
    layerOrder,
    nextLayerId,
    unusedLayerIds,
    activeLayer: newLayerId,
  };
};

const removeLayer = (prevState: DrawingState, layerId: number): DrawingState => {
  if (layerId === bottomLayerID) {
    throw new Error('Cannot remove the bottom layer.');
  }

  const layerOrder = prevState.layerOrder.slice();
  const unusedLayerIds = prevState.unusedLayerIds.slice();
  const layers: { [layerId: number]: DrawingLayer } = { ...prevState.layers };
  let pictureReferences = prevState.pictureReferences;

  visitPictureReferencesInDrawingLayer(layers[layerId], (id) => {
    pictureReferences = decrementReference(pictureReferences, id);
  });

  delete layers[layerId];
  unusedLayerIds.push(layerId);
  const index = layerOrder.indexOf(layerId);
  if (index > -1) {
    layerOrder.splice(index, 1);
  }

  return {
    ...prevState,
    layers,
    layerOrder,
    unusedLayerIds,
    pictureReferences,
    activeLayer: layerOrder[layerOrder.length - 1],
  };
};

const clearAllLearningPenRects = (prevState: DrawingState): DrawingState => {
  const layerOrder = prevState.layerOrder.slice();
  const unusedLayerIds = prevState.unusedLayerIds.slice();
  const layers: { [layerId: number]: DrawingLayer } = { ...prevState.layers };

  for (const key in layers) {
    if (layers[key].shape.kind !== ShapeKind.LearningPenRect) {
      continue;
    }

    const layerId = parseInt(key, 10);

    if (layerId === bottomLayerID) {
      throw new Error('Cannot remove the bottom layer.');
    }

    delete layers[layerId];
    unusedLayerIds.push(layerId);
    const index = layerOrder.indexOf(layerId);
    if (index > -1) {
      layerOrder.splice(index, 1);
    }
  }

  return {
    ...prevState,
    layers,
    layerOrder,
    unusedLayerIds,
    activeLayer: layerOrder[layerOrder.length - 1],
  };
};

const clearAllObjects = (prevState: DrawingState): DrawingState => {
  const layerOrder = prevState.layerOrder.slice();
  const unusedLayerIds = prevState.unusedLayerIds.slice();
  const layers: { [layerId: number]: DrawingLayer } = { ...prevState.layers };
  let pictureReferences = prevState.pictureReferences;

  for (const key in layers) {
    if (layers[key].shape.kind === ShapeKind.None) {
      continue;
    }
    if (layers[key].shape.kind === ShapeKind.LearningPenRect) {
      continue;
    }

    const layerId = parseInt(key, 10);

    if (layerId === bottomLayerID) {
      throw new Error('Cannot remove the bottom layer.');
    }
    visitPictureReferencesInDrawingLayer(layers[layerId], (id) => {
      pictureReferences = decrementReference(pictureReferences, id);
    });

    delete layers[layerId];
    unusedLayerIds.push(layerId);
    const index = layerOrder.indexOf(layerId);
    if (index > -1) {
      layerOrder.splice(index, 1);
    }
  }

  return {
    ...prevState,
    layers,
    layerOrder,
    unusedLayerIds,
    pictureReferences,
    activeLayer: layerOrder[layerOrder.length - 1],
  };
};

const clearAllHandwriting = (state: DrawingState): DrawingState => {
  const layers: { [layerId: number]: DrawingLayer } = { ...state.layers };
  for (const layerID in layers) {
    layers[layerID] = { ...layers[layerID], currentImageTiling: new Tiling<string>() };
  }

  return { ...state, layers, pictureReferences: {} };
};

const removeMultiroleLinkLayer = (
  prevState: DrawingState, multiroleLinkID: string,
): DrawingState => {
  const { layers } = prevState;
  for (const layerID in layers) {
    const { shape } = layers[layerID];
    if (shape.kind === ShapeKind.MultiroleLink) {
      if (shape.multiroleLink.id === multiroleLinkID) {
        return removeLayer(prevState, parseInt(layerID, 10));
      }
    }
  }
  return prevState;
};

export const moveStraightLineShape = (
  target: Readonly<StraightLineShape>, dx: number, dy: number,
): StraightLineShape => {
  const { begin, end } = target.line;
  return {
    ...target,
    line: {
      begin: { x: begin.x + dx, y: begin.y + dy },
      end: { x: end.x + dx, y: end.y + dy },
    },
  };
};

const moveShape = (target: Readonly<ShapeInfo>, dx: number, dy: number): ShapeInfo => {
  switch (target.kind) {
    case ShapeKind.Rect:
    case ShapeKind.Square:
    case ShapeKind.Oval:
    case ShapeKind.Circle:
    case ShapeKind.Triangle:
    case ShapeKind.EquilateralTriangle:
    case ShapeKind.ImageFile:
    case ShapeKind.MultiroleLink:
    case ShapeKind.Sticky:
    case ShapeKind.Stamp:
    case ShapeKind.LearningPenRect:
      return {
        ...target,
        x: target.x + dx,
        y: target.y + dy,
      };
    case ShapeKind.StraightLine:
    case ShapeKind.AxisAlignedStraightLine: {
      return moveStraightLineShape(target, dx, dy);
    }
    case ShapeKind.Polyline:
    case ShapeKind.Polygon: {
      const vertices = target.vertices.map((vertex: Point) => {
        return { x: vertex.x + dx, y: vertex.y + dy };
      });
      return { ...target, vertices };
    }
    case ShapeKind.Balloon:
      return {
        ...target,
        x: target.x + dx,
        y: target.y + dy,
        arrowPoint: {
          x: target.arrowPoint.x + dx,
          y: target.arrowPoint.y + dy,
        },
      };
    default:
      return target;
  }
};

export const transformVertices = (
  vertices: ReadonlyArray<Point>,
  delta: TransformDelta,
): ReadonlyArray<Point> => {
  const originalRect = getRectCoveringAllPoints(vertices);
  if (originalRect.width === 0 && originalRect.height === 0) {
    return vertices; // 全て同じ位置の点である場合はなにもしない
  }

  const rect = {
    x: originalRect.x + delta.x,
    y: originalRect.y + delta.y,
    width: originalRect.width + delta.width,
    height: originalRect.height + delta.height,
  };

  return vertices.map((vertex: Point) => {
    // move
    let x = vertex.x + delta.x;
    let y = vertex.y + delta.y;

    // scale
    if (delta.width !== 0 && originalRect.width !== 0) {
      x = (x - rect.x) * (rect.width / originalRect.width) + rect.x;
    }
    if (delta.height !== 0 && originalRect.height !== 0) {
      y = (y - rect.y) * (rect.height / originalRect.height) + rect.y;
    }

    return { x, y };
  });
};

export const resizeShape = (
  target: Readonly<ShapeInfo>, dx: number, dy: number, dwidth: number, dheight: number,
): ShapeInfo => {
  switch (target.kind) {
    case ShapeKind.Rect:
    case ShapeKind.Square:
    case ShapeKind.Oval:
    case ShapeKind.Circle:
    case ShapeKind.Triangle:
    case ShapeKind.EquilateralTriangle:
    case ShapeKind.Sticky:
    case ShapeKind.Balloon:
    case ShapeKind.ImageFile:
    case ShapeKind.Stamp:
    case ShapeKind.LearningPenRect: {
      let newX: number = target.x + dx;
      let newY: number = target.y + dy;
      let newWidth: number = target.width + dwidth;
      let newHeight: number = target.height + dheight;
      if (newWidth < 0) {
        newX += newWidth;
        newWidth = -newWidth;
      }
      if (newHeight < 0) {
        newY += newHeight;
        newHeight = -newHeight;
      }
      return {
        ...target,
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight,
      };
    }
    case ShapeKind.Polyline:
    case ShapeKind.Polygon: {
      const delta: TransformDelta = {
        x: dx, y: dy, width: dwidth, height: dheight, origin: unnamedPoint,
      };
      return {
        ...target,
        vertices: transformVertices(target.vertices, delta),
      };
    }
    default:
      return target;
  }
};

const moveLayer = (target: Readonly<DrawingLayer>, dx: number, dy: number): DrawingLayer => {
  const { imageOffsetX, imageOffsetY, currentImageTiling } = target;
  let nextImageTiling = currentImageTiling;

  /**
   * Rounding is to prevent blur artifact made by fractional number which revises the bitmap
   * a little in each time stroke is added.
   */
  let nextImageOffsetX = Math.round(imageOffsetX + dx);
  let nextImageOffsetY = Math.round(imageOffsetY + dy);

  const columnShift = Math.floor(nextImageOffsetX / imageTileSize);
  const rowShift = Math.floor(nextImageOffsetY / imageTileSize);
  nextImageOffsetX -= columnShift * imageTileSize;
  nextImageOffsetY -= rowShift * imageTileSize;
  if (rowShift !== 0 || columnShift !== 0) {
    nextImageTiling = new Tiling<string>();
    currentImageTiling.forEach((row, column, pictureID) => {
      nextImageTiling = nextImageTiling.plus(row + rowShift, column + columnShift, pictureID);
    });
  }

  return {
    ...target,
    shape: moveShape(target.shape, dx, dy),
    currentImageTiling: nextImageTiling,
    imageOffsetX: nextImageOffsetX,
    imageOffsetY: nextImageOffsetY,
  };
};

const resizeLayer = (
  target: Readonly<DrawingLayer>, dx: number, dy: number, dwidth: number, dheight: number,
): DrawingLayer => {
  return {
    ...target,
    shape: resizeShape(target.shape, dx, dy, dwidth, dheight),
  };
};

const changeShapeText = (target: Readonly<ShapeInfo>, text: string): ShapeInfo => {
  switch (target.kind) {
    case ShapeKind.Balloon:
    case ShapeKind.Sticky:
      return {
        ...target,
        text,
      };
    default:
      return target;
  }
};

const changeText = (target: Readonly<DrawingLayer>, text: string): DrawingLayer => {
  return {
    ...target,
    shape: changeShapeText(target.shape, text),
  };
};

const moveBalloonArrowPoint = (target: Readonly<BalloonShape>, offset: Offset): BalloonShape => ({
  ...target,
  arrowPoint: {
    x: target.arrowPoint.x + offset.x,
    y: target.arrowPoint.y + offset.y,
  },
});

export const moveStraightLineShapeVertex = (
  target: Readonly<StraightLineShape>, offset: Offset,
): StraightLineShape => {
  const { begin, end } = target.line;
  const line = (offset.origin.name === PointName.LineBegin)
    ? { end, begin: { x: begin.x + offset.x, y: begin.y + offset.y } }
    : { begin, end: { x: end.x + offset.x, y: end.y + offset.y } };

  if (target.kind === ShapeKind.AxisAlignedStraightLine) {
    return offset.origin.name === PointName.LineBegin
      ? { ...target, line: getAxisAlignedLineFromPoints(line.end, line.begin) }
      : { ...target, line: getAxisAlignedLineFromPoints(line.begin, line.end) };
  }

  return { ...target, line };
};

export const moveMatchingVertex = (
  vertices: ReadonlyArray<Point>, offset: Offset,
): ReadonlyArray<Point> => {
  return vertices.map((vertex: Point) => {
    return isPointEqual(vertex, offset.origin)
      ? { x: vertex.x + offset.x, y: vertex.y + offset.y }
      : vertex;
  });
};

const moveLayerPoint = (target: Readonly<DrawingLayer>, offset: Offset): DrawingLayer => {
  switch (target.shape.kind) {
    case ShapeKind.Balloon: {
      return {
        ...target,
        shape: moveBalloonArrowPoint(target.shape, offset),
      };
    }
    case ShapeKind.StraightLine:
    case ShapeKind.AxisAlignedStraightLine: {
      return {
        ...target,
        shape: moveStraightLineShapeVertex(target.shape, offset),
      };
    }
    case ShapeKind.Polyline:
    case ShapeKind.Polygon: {
      return {
        ...target,
        shape: {
          ...target.shape,
          vertices: moveMatchingVertex(target.shape.vertices, offset),
        },
      };
    }
    default:
      throw new Error('Illegal argument.');
  }
};

const foldNote = (prevState: DrawingState, layerId: number, isFolded: boolean): DrawingState => {
  if (layerId === bottomLayerID) {
    throw new Error('Cannot fold the bottom layer.');
  }

  const target = prevState.layers[layerId].shape;
  switch (target.kind) {
    case ShapeKind.Sticky:
    case ShapeKind.Balloon: {
      const layers: { [layerId: number]: DrawingLayer } = { ...prevState.layers };
      const shape = { ...target, isFolded };
      layers[layerId] = { ...layers[layerId], shape };
      return { ...prevState, layers };
    }
    default:
      throw new Error('Illegal argument.');
  }
};

const toggleTransparencyOfLearningPen =
  (prevState: DrawingState, layerId: number, isTransparency: boolean): DrawingState => {
    if (layerId === bottomLayerID) {
      throw new Error('Cannot fold the bottom layer.');
    }

    const target = prevState.layers[layerId].shape;
    const layers: { [layerId: number]: DrawingLayer } = { ...prevState.layers };
    const shape = { ...target, alpha: isTransparency ? 1 : 0.5 };
    layers[layerId] = { ...layers[layerId], shape };
    return { ...prevState, layers };
  };

const toggleTransparencyOfAllLearningPens =
  (prevState: DrawingState, isTransparency: boolean): DrawingState => {

    const layers: { [layerId: number]: DrawingLayer } = { ...prevState.layers };
    for (const layerID in layers) {
      if (layers[layerID].shape.kind === ShapeKind.LearningPenRect) {
        const shape = { ...layers[layerID].shape, alpha: isTransparency ? 0.5 : 1 };
        layers[layerID] = { ...layers[layerID], shape };
      }
    }

    return { ...prevState, layers };
  };

export const drawing = (state: DrawingState = init(), action: DrawingAction) => {
  switch (action.type) {
    case ActionNames.ACTIVATE_LAYER: {
      if (state.activeLayer === action.layerID) {
        return state;
      }

      return {
        ...state,
        activeLayer: action.layerID,
      };
    }

    case ActionNames.REPLACE_HANDWRITING_TILES: {
      if (action.tiles.isEmpty) {
        return state;
      }

      const layers: { [layerId: number]: DrawingLayer } = { ...state.layers };
      let currentImageTiling = layers[state.activeLayer].currentImageTiling;
      let pictureReferences = state.pictureReferences;

      action.tiles.forEach((row, column, entry) => {
        if (currentImageTiling.has(row, column)) {
          pictureReferences = decrementReference(
            pictureReferences,
            currentImageTiling.at(row, column)!,
          );
        }

        currentImageTiling = currentImageTiling.plus(row, column, entry.id);
        pictureReferences = incrementReference(pictureReferences, entry.id);
      });

      layers[state.activeLayer] = {
        ...layers[state.activeLayer],
        currentImageTiling,
      };
      return { ...state, layers, pictureReferences };
    }

    case ActionNames.ADD_FIGURE:
    case ActionNames.ADD_STICKY:
    case ActionNames.ADD_BALLOON:
    case ActionNames.ADD_POLYLINE:
    case ActionNames.ADD_POLYGON:
    case ActionNames.ADD_STRAIGHT_LINE:
    case ActionNames.PASTE_MULTIROLE_LINK: {
      const { type, ...shape } = action;
      return appendLayer(state, shape);
    }

    case ActionNames.PASTE_IMAGE: {
      const { source, x, y, alpha } = action;
      const width = source.picture.width * fundamentalSizeMultiplier;
      const height = source.picture.height * fundamentalSizeMultiplier;
      const pictureReferences = incrementReference(state.pictureReferences, source.id);
      return appendLayer({ ...state, pictureReferences }, {
        x, y, width, height, alpha, kind: ShapeKind.ImageFile, pictureID: source.id,
      });
    }

    case ActionNames.ADD_STAMP: {
      const {  x, y, width, height, stampId } = action;
      return appendLayer({ ...state }, {
        x, y, width, height, stampId, kind: ShapeKind.Stamp,
      });
    }

    case ActionNames.MOVE_LAYER: {
      const layers: { [layerId: number]: DrawingLayer } = { ...state.layers };
      layers[state.activeLayer] = moveLayer(state.layers[state.activeLayer], action.dx, action.dy);
      return {
        ...state,
        layers,
      };
    }

    case ActionNames.REMOVE_LAYER: {
      return removeLayer(state, action.layerID);
    }

    case ActionNames.CLEAR_ALL_LEARNING_PEN_RECTS: {
      return clearAllLearningPenRects(state);
    }

    case ActionNames.CLEAR_ALL_OBJECTS: {
      return clearAllObjects(state);
    }

    case ActionNames.CLEAR_ALL_HANDWRITING: {
      return clearAllHandwriting(state);
    }

    case ActionNames.REMOVE_MULTIROLE_LINK: {
      return removeMultiroleLinkLayer(state, action.multiroleLinkID);
    }

    case ActionNames.CLEAR_EVERYTHING: {
      return blankDrawingState;
    }

    case ActionNames.RESIZE_LAYER: {
      const layers: { [layerId: number]: DrawingLayer } = { ...state.layers };
      layers[state.activeLayer] = resizeLayer(
        state.layers[state.activeLayer], action.dx, action.dy, action.dwidth, action.dheight,
      );
      return {
        ...state,
        layers,
      };
    }

    case ActionNames.CHANGE_TEXT: {
      const layers: { [layerId: number]: DrawingLayer } = { ...state.layers };
      layers[state.activeLayer] = changeText(state.layers[state.activeLayer], action.text);
      return {
        ...state,
        layers,
      };
    }

    case ActionNames.MOVE_POINT: {
      const layers: { [layerId: number]: DrawingLayer } = { ...state.layers };
      layers[state.activeLayer] = moveLayerPoint(
        state.layers[state.activeLayer], action.offset,
      );
      return { ...state, layers };
    }

    case ActionNames.FOLD_NOTE: {
      return foldNote(state, action.layerID, action.isFolded);
    }

    case ActionNames.TOGGLE_TRANSPARENCY_OF_LEARNING_PEN: {
      return toggleTransparencyOfLearningPen(state, action.layerID, action.isTransparency);
    }

    case ActionNames.TOGGLE_TRANSPARENCY_OF_ALL_LEARNING_PENS: {
      return toggleTransparencyOfAllLearningPens(state, action.isTransparency);
    }

    default:
      return state;
  }
};

export function isReadyToPaint<Image>(
  drawing: DrawingState,
  pictureStorage: PictureStorageState<Image>,
): boolean {
  for (const id in drawing.pictureReferences) {
    if (!(id in pictureStorage.items)) {
      throw new Error('Illegal state');
    }

    if (pictureStorage.items[id].image === null) {
      return false;
    }
  }

  return true;
}
